// src/pages/FileRepositoryPage.js
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { apiClient } from '../api/apiClient';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import pLimit from 'p-limit';
import styled from 'styled-components';
import PageShell from '../components/PageShell';
import { CornerCard, PrimaryButton } from '../components/ui';
import {
    SubTitle, ErrorNote, AdminButton, ButtonRow, TableWrap, Table, Th, Td, Tr,
} from './admincomponents/adminStyles';
import { color, font } from '../theme/tokens';

const UploadCard = styled(CornerCard)`
    margin-bottom: 32px;
`;

const UploadRow = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 14px;
`;

const FilePicker = styled.label`
    position: relative;
    flex: 1 1 320px;
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 14px;
    height: 48px;
    padding: 0 6px 0 0;
    border: 1px solid ${color.line2};
    background: rgba(7, 2, 5, 0.7);
    cursor: pointer;
    transition: border-color 0.2s ease;

    &:hover { border-color: ${color.bloodHi}; }
    &:focus-within { outline: 2px solid ${color.bloodHi}; outline-offset: 2px; }

    input {
        position: absolute;
        width: 1px;
        height: 1px;
        margin: -1px;
        padding: 0;
        border: 0;
        overflow: hidden;
        clip: rect(0 0 0 0);
        white-space: nowrap;
    }

    .btn {
        flex-shrink: 0;
        display: flex;
        align-items: center;
        height: 100%;
        padding: 0 18px;
        border-right: 1px solid ${color.line2};
        font-family: ${font.display};
        font-size: 14px;
        letter-spacing: 0.2em;
        color: ${color.text2};
    }

    .name {
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        font-size: 14px;
        color: ${({ $hasFile }) => ($hasFile ? color.text : color.text3)};
    }

    ${({ $disabled }) => $disabled && 'opacity: 0.5; cursor: not-allowed;'}
`;

const ProgressBlock = styled.div`
    margin-top: 20px;
`;

const ProgressHead = styled.div`
    display: flex;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 8px;
    font-size: 14px;
    color: ${color.text};
    .pct { font-family: ${font.mono}; color: ${color.bloodHi}; }
`;

const ProgressTrack = styled.div`
    width: 100%;
    height: 4px;
    background: ${color.line};
    .bar { height: 100%; background: ${color.blood}; transition: width 0.3s linear; }
`;

const ProgressMeta = styled.div`
    display: flex;
    justify-content: space-between;
    gap: 12px;
    margin-top: 8px;
    font-size: 13px;
    color: ${color.text3};
    b { font-family: ${font.mono}; font-weight: 400; color: ${color.text2}; }
`;

const EmptyCell = styled(Td)`
    padding: 32px 14px;
    text-align: center;
    font-family: ${font.display};
    letter-spacing: 0.2em;
    color: ${color.text3};
`;


const CHUNK_SIZE = 5 * 1024 * 1024;
const limit = pLimit(4);

const FileRepositoryPage = () => {
    const [files, setFiles] = useState([]);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);
    const [selectedFile, setSelectedFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadSpeed, setUploadSpeed] = useState(0); 
    const [remainingTime, setRemainingTime] = useState(null);
    const [uploadStatus, setUploadStatus] = useState('');
 
    const { user } = useAuth();
    const navigate = useNavigate();
 
    useEffect(() => {
        if (user && (user.role !== 'admin' && user.role !== 'site_owner')) {
            navigate('/');
        }
    }, [user, navigate]);
 
    const fetchFiles = useCallback(async () => {
        try {
            setLoading(true);
            const response = await apiClient.get('/admin/repository/files');
            // 按最后修改时间排序，最新的在前面
            const sortedFiles = response.data.sort((a, b) => new Date(b.lastModified) - new Date(a.lastModified));
            setFiles(sortedFiles);
            setError('');
        } catch (err) {
            setError(err.response?.data?.message || '无法加载文件列表。');
            console.error("Fetch files error:", err);
        } finally {
            setLoading(false);
        }
    }, []);
 
    useEffect(() => {
        if (user && (user.role === 'admin' || user.role === 'site_owner')) {
          fetchFiles();
        }
    }, [user, fetchFiles]); // 依赖 user.role，避免不必要的重复请求
 
    // --- 修改：下载处理改为获取直连 URL ---
    const handleDownload = async (filename) => {
        try {
            // 1. 从后端获取包含令牌的直接下载 URL
            const response = await apiClient.get(`/admin/repository/generate-download-url/${encodeURIComponent(filename)}`);
            const { url } = response.data;
            // 2. 在新标签页中打开 URL，浏览器将直接从存储服务器下载
            window.open(url, '_blank');
        } catch (err) {
            setError(err.response?.data?.message || '获取下载链接失败。');
            console.error("Download link error:", err);
        }
    };
 
    const handleFileChange = (event) => {
        setSelectedFile(event.target.files[0]);
    };
 
    // --- 修改：上传处理改为“获取指令 -> 直连上传”流程 ---
    const handleUpload = async () => {
        if (!selectedFile) {
            setError('请先选择一个文件。');
            return;
        }
 
        setUploading(true);
        setError('');
        setUploadProgress(0);
        setUploadStatus('正在初始化上传...');
        let uploadedChunks = 0; // 用于精确计算进度
 
        try {
            // 1. 初始化上传 (这部分不变)
            const initResponse = await apiClient.post('/admin/repository/initiate-upload', {
                filename: selectedFile.name,
            });
            const { uploadId, token, uploadChunkUrl } = initResponse.data;
 
            // 2. 切分文件并准备上传任务
            const totalChunks = Math.ceil(selectedFile.size / CHUNK_SIZE);
            const chunkPromises = [];
 
            setUploadStatus(`文件分片中 (共 ${totalChunks} 片)...`);
 
            for (let i = 0; i < totalChunks; i++) {
                const start = i * CHUNK_SIZE;
                const end = Math.min(start + CHUNK_SIZE, selectedFile.size);
                const chunk = selectedFile.slice(start, end);
 
                // 3. 改造上传循环，使用 limit 函数包装你的上传逻辑
                const promise = limit(() => {
                    const formData = new FormData();
                    formData.append('uploadId', uploadId);
                    formData.append('token', token);
                    formData.append('filename', selectedFile.name);
                    formData.append('chunkIndex', i);
                    formData.append('chunk', chunk, `${selectedFile.name}.part${i}`);
 
                    // 返回原始的 axios promise
                    return axios.post(uploadChunkUrl, formData, {
                        // 使用原始 axios，避免 apiClient 附加不必要的头
                    }).then(() => {
                        // 每当一个分片上传成功，就更新总进度
                        uploadedChunks++;
                        const newProgress = Math.round((uploadedChunks / totalChunks) * 100);
                        setUploadProgress(newProgress);
                    });
                });
                chunkPromises.push(promise);
            }
 
            // 4. 并发执行所有分片上传 (现在 Promise.all 是安全的)
            setUploadStatus(`正在上传分片 (并发数: 4)...`);
            await Promise.all(chunkPromises);
 
            // 5. 所有分片上传完毕，通知主站进行合并 (这部分不变)
            setUploadStatus('所有分片上传完成，正在请求合并...');
            await apiClient.post('/admin/repository/complete-upload', {
                filename: selectedFile.name,
                uploadId,
                token,
                totalChunks,
            });
 
            setUploadStatus('上传成功！');
            setSelectedFile(null);
            document.getElementById('file-input').value = null;
            await fetchFiles();
 
        } catch (err) {
            console.error("Upload failed:", err);
            const errorMessage = err.response?.data?.error || err.response?.data?.message || '上传失败，请检查控制台获取详情。';
            setError(`上传失败: ${errorMessage}`);
            setUploadStatus('上传失败');
        } finally {
            setUploading(false);
        }
    };
 
    const handleDelete = async (filename) => {
        if (!window.confirm(`确定要删除文件 "${filename}" 吗？此操作无法撤销。`)) {
            return;
        }
        try {
            await apiClient.delete(`/admin/repository/files/${encodeURIComponent(filename)}`);
            setFiles(files.filter(file => file.name !== filename));
        } catch (err) {
            setError(err.response?.data?.message || '删除失败。');
            console.error("Delete error:", err);
        }
    };
    
    // 文件大小格式化
    const formatBytes = (bytes, decimals = 2) => {
        if (!+bytes) return '0 Bytes'
        const k = 1024;
        const dm = decimals < 0 ? 0 : decimals;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`
    }

    // --- 新增：格式化速度的辅助函数 ---
    const formatSpeed = (bytesPerSecond) => {
        if (!bytesPerSecond || bytesPerSecond < 0) return '0 B/s';
        const k = 1024;
        const sizes = ['B/s', 'KB/s', 'MB/s', 'GB/s', 'TB/s'];
        const i = Math.floor(Math.log(bytesPerSecond) / Math.log(k));
        return `${parseFloat((bytesPerSecond / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
    };

    // --- 新增：格式化剩余时间的辅助函数 ---
    const formatTime = (seconds) => {
        if (seconds === Infinity || isNaN(seconds) || seconds < 0) {
            return '计算中...';
        }
        if (seconds < 60) {
            return `约 ${Math.round(seconds)} 秒`;
        }
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = Math.round(seconds % 60);
        return `约 ${minutes} 分 ${remainingSeconds} 秒`;
    };


    if (!user || (user.role !== 'admin' && user.role !== 'site_owner')) return null;

    return (
        <PageShell title="倉庫" en="REPOSITORY" wide>
            <UploadCard>
                <SubTitle>上传新文件</SubTitle>
                <UploadRow>
                    <FilePicker htmlFor="file-input" $hasFile={!!selectedFile} $disabled={uploading}>
                        <input
                            id="file-input"
                            type="file"
                            onChange={handleFileChange}
                            disabled={uploading} // 上传时禁用选择
                        />
                        <span className="btn">选择文件</span>
                        <span className="name">{selectedFile ? selectedFile.name : '尚未选择文件'}</span>
                    </FilePicker>
                    <PrimaryButton
                        type="button"
                        onClick={handleUpload}
                        disabled={!selectedFile || uploading}
                    >
                        {uploading ? '上传中…' : '确认上传'}
                    </PrimaryButton>
                </UploadRow>

                {/* --- 上传进度显示区域 --- */}
                {uploading && (
                    <ProgressBlock role="status" aria-live="polite">
                        <ProgressHead>
                            <span>{uploadStatus}</span>
                            <span className="pct">{uploadProgress}%</span>
                        </ProgressHead>
                        <ProgressTrack role="progressbar" aria-valuenow={uploadProgress} aria-valuemin={0} aria-valuemax={100} aria-label="上传进度">
                            <div className="bar" style={{ width: `${uploadProgress}%` }}></div>
                        </ProgressTrack>
                        <ProgressMeta>
                           <span>速度 <b>{formatSpeed(uploadSpeed)}</b></span>
                           <span>剩余时间 <b>{remainingTime ?? "计算中…"}</b></span>
                        </ProgressMeta>
                    </ProgressBlock>
                )}

                {/* 错误提示移到进度条下方，避免布局跳动 */}
                {error && !uploading && <ErrorNote role="alert" style={{ marginTop: 16, marginBottom: 0 }}>{error}</ErrorNote>}
            </UploadCard>

            <SubTitle>仓库文件</SubTitle>
            <TableWrap>
                <Table>
                    <thead>
                        <tr>
                            <Th>文件名</Th>
                            <Th>大小</Th>
                            <Th>操作</Th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                             <tr><EmptyCell colSpan="3">载入中…</EmptyCell></tr>
                        ) : files.length > 0 ? (
                            files.map((file) => (
                                <Tr key={file.name}>
                                    <Td style={{ wordBreak: 'break-all' }}>{file.name}</Td>
                                    <Td $mono $dim style={{ whiteSpace: 'nowrap' }}>{formatBytes(file.size)}</Td>
                                    <Td>
                                        <ButtonRow style={{ flexWrap: 'nowrap' }}>
                                            <AdminButton type="button" $small onClick={() => handleDownload(file.name)}>下载</AdminButton>
                                            <AdminButton type="button" $small $variant="danger" onClick={() => handleDelete(file.name)}>删除</AdminButton>
                                        </ButtonRow>
                                    </Td>
                                </Tr>
                            ))
                        ) : (
                            <tr><EmptyCell colSpan="3">仓库中没有文件。</EmptyCell></tr>
                        )}
                    </tbody>
                </Table>
            </TableWrap>
        </PageShell>
    );
};

export default FileRepositoryPage;
